import { FormControlLabel, TableFooter } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {
  type ChangeEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';
import type { NestedKeyOf } from '@core/utils/types';
import { ROWS_PER_PAGE_OPTS } from '@web/constants/app';

import { useToast } from '../../providers/ToastProvider/useToast';
import type { FilterByOpts, NamedFilterOpts } from '../../utils/ApiClient';
import { BulkSelectOverlay } from './components/BulkSelectOverlay';
import { supportedColumnFilterOperations } from './components/ColumnFilter';
import { DataTableBody } from './components/DataTableBody';
import { FilterTabs } from './components/FilterTabs';
import { SlabTableRowCells } from './components/SlabTableRowCells';
import { TableHead } from './components/TableHead';
import { Toolbar } from './components/Toolbar/Toolbar';
import type { TypeAheadTables } from './components/types';
import type { FilterTabLabelsOpts } from './hooks/useFilterTabLabels';
import type { Column, ColumnConfig, TableDataModel } from './TableDataModel';
import { useTableState } from './useTableState';

const getInitialSortOrder = <TRow extends { id: string }>(
  columns: ReadonlySet<Column<TRow>>,
  id: NestedKeyOf<TRow>,
): Enums.SortDirection => {
  const column = [...columns].find((col) => col.id === id);
  return column?.initialSortOrder ?? Enums.SortDirection.Ascending;
};

export type FilterCheckbox<TRow extends { id: string }> = {
  /** Labels must be unique amongst the array. */
  label: string;
  filter: NamedFilterOpts<TRow>;
  /** @default false */
  defaultChecked?: boolean;
};
type DataTableProps<TRow extends { id: string }, TAgg extends { id: string }> = {
  tableName?: string;
  typeaheadTableName?: TypeAheadTables;
  tableModel: TableDataModel<TRow, TAgg>;

  /**
   * The title of the table.
   * This is used to display the title of the table in the bulk select overlay.
   * @default undefined
   */
  tableTitle?: string;
  /**
   * Given the currently filtered rows (after tab + free search filtering is applied),
   * construct a set of rows to keep sticky at the bottom of the table.
   */
  constructStickyRows?: (aggregations: { [id: string]: TAgg }) => TRow[];
  /**
   * If true, include select checkboxes.
   *
   * @default false
   */
  includeSelect?: boolean;
  onSelectRow?: (rowIDs: readonly string[]) => void;
  selectedRowIDs?: readonly string[];

  /**
   * If true, include a toolbar with filters and actions.
   *
   * @default true
   */
  showToolbar?: boolean;
  /**
   * If true, include a column filter icon in the column headers.
   *
   * @default true
   */
  showColumnFilter?: boolean;
  /**
   * If defined, allow row edits for rows where canEditRow returns true.
   * @default undefined
   */
  onEditPress?: (value: TRow) => void;

  /**
   * Action to perform for archiving/restoring rows.
   * If not defined, the archive/restore buttons are never shown.
   * @default undefined
   */
  onArchivePress?: (row: TRow) => void;

  /**
   * Tells `onArchivePress` whether a row is already archived, to pick the archive or restore icon.
   * @default undefined
   */
  isRowArchived?: (row: TRow) => boolean;

  /**
   * When false, hides row archive/restore even if `onArchivePress` is set.
   * @default true
   */
  canArchive?: boolean;

  /**
   * Extra row actions, rendered in the sticky right-pinned column after the edit and archive
   * buttons. Return bare buttons — the cell provides the layout. Use only for actions that
   * `onEditPress` and `onArchivePress` do not cover.
   * @default undefined
   */
  renderStickyActions?: (row: TRow) => ReactElement;

  /**
   * If defined, allow row edits for rows where canEditRow returns true.
   * @default undefined
   */
  onBulkEditPress?: () => void;

  /**
   * Custom actions rendered in the bulk select overlay, left of the edit button.
   * Only visible while rows are selected.
   * @default undefined
   */
  bulkActionsSlot?: ReactNode;

  /**
   * If specified, disable row editing for rows where this callback returns false.
   * @default () => true
   */
  canEditRow?: (value: TRow) => boolean;

  /**
   * If specified, highlight rows based on the callback result.
   * Return a color string to highlight the row, or undefined for no highlighting.
   * @default undefined
   */
  getRowHighlight?: (row: TRow) => string | undefined;

  defaultFilters?: FilterByOpts<TRow>[];

  filterTabs?: Omit<FilterTabLabelsOpts<TRow, TAgg>, 'aggregations'>;

  /**
   * Render prop injecting custom filter components (e.g. location/market/division selectors)
   * into the toolbar. Receives tableData so callers can use makeSetNamedFilter and
   * tableData.getFilter to wire filter state.
   *
   * @example
   * renderToolbarFilters={(tableData) => <LocationMarketDivisionFilters tableData={tableData} />}
   */
  renderToolbarFilters?: (tableData: TableDataModel<TRow, TAgg>) => ReactElement;

  /** @default '65vh' */
  tableMaxHeight?: string;
  /** @default undefined */
  includePrint?: {
    title: string;
  };
  /** Exports every row matching the current filters and sort, with the displayed columns. */
  includeExport?: {
    title: string;
  };

  /** Optional array of filter-capable checkboxes. */
  filterCheckboxes?: Array<FilterCheckbox<TRow>>;
  onClearState?: () => void;
  /**
   * determines the content of the expanded row
   */
  renderRowDetails?: (value: TRow) => ReactElement;

  /**
   * If true, the row details panel inner box is rendered sticky (left-pinned).
   *
   * @default false
   */
  stickyRowDetails?: boolean;

  /**
   * if the footer is visible
   *
   * @default true
   */
  showFooter?: boolean;

  /**
   * used to indicate if the table is nested in the row details.
   * these tables have different design.
   *
   * @default false
   * */
  nested?: boolean;

  /**
   * if true, the user has permission to bulk edit the table
   * @default false
   */
  hasBulkEditPermission?: boolean;

  /**
   * are row details highlighted using background color used for selected items.
   */
  highlightRowDetails?: boolean;
};

export type SharedDataTableProps<TRow extends { id: string }, TAgg extends { id: string }> = Omit<
  DataTableProps<TRow, TAgg>,
  'tableModel' | 'defaultFilters'
> & {
  /**
   * Optional column override to update the label or isDisplayed property.
   *
   * If `null` is passed in, the column is omitted.
   */
  optionalColumnOverrides?: ColumnConfig<TRow>[];
  optionalColumnRemovals?: NestedKeyOf<TRow>[];
};

type ApplyColumnChangesParams<TRow extends { id: string }, TAgg extends { id: string }> = Pick<
  SharedDataTableProps<TRow, TAgg>,
  'optionalColumnOverrides' | 'optionalColumnRemovals'
> & {
  columns: ColumnConfig<TRow>[];
};

/**
 * Given an array of columns, overrides, and omissions, return the expected columns
 * using the override and omission values passed.
 */
export const ApplyColumnChanges = <TRow extends { id: string }, TAgg extends { id: string }>({
  columns,
  optionalColumnOverrides,
  optionalColumnRemovals,
}: ApplyColumnChangesParams<TRow, TAgg>): ColumnConfig<TRow>[] =>
  columns
    .map((col) => {
      if (optionalColumnRemovals !== undefined && optionalColumnRemovals.includes(col.id)) {
        return null;
      }
      const matchingColOverride = optionalColumnOverrides?.find(
        (colOverride) => colOverride?.id !== undefined && colOverride.id === col.id,
      );
      // Null was passed in, so omit it.
      if (matchingColOverride === null) {
        return null;
      }
      if (matchingColOverride !== undefined && matchingColOverride.id === col.id) {
        return matchingColOverride;
      }
      // No override so use the default value
      return col;
    })
    .filter((col): col is ColumnConfig<TRow> => col !== null);

const getColumnFilter = <TRow extends { id: string }, TAgg extends { id: string }>({
  filterCheckboxes,
  defaultFilters,
  tableData,
}: {
  filterCheckboxes: FilterCheckbox<TRow>[];
  defaultFilters: FilterByOpts<TRow>[];
  tableData: TableDataModel<TRow, TAgg>;
}): FilterByOpts<TRow>[] => {
  const predefinedFilters = [
    ...new Set(
      [...filterCheckboxes].flatMap((it) =>
        hasValue(it.filter) && 'name' in it.filter ? [it.filter.name] : [],
      ),
    ),
    ...defaultFilters.flatMap((it) => ('name' in it ? [it.name] : [])),
    'marketId',
    'plantId',
    'locationId',
    'divisionId',
    'productCategory',
  ];

  return tableData
    .getAllColumnFilters()
    .filter((filter) => 'name' in filter && !predefinedFilters.includes(filter?.name));
};

export const DataTable = <TRow extends { id: string }, TAgg extends { id: string }>({
  tableName,
  typeaheadTableName,
  tableModel: tableData,
  constructStickyRows,
  includeSelect = false,
  showToolbar = true,
  showFooter = true,
  showColumnFilter = true,
  onEditPress,
  onArchivePress,
  isRowArchived,
  canArchive = true,
  renderStickyActions,
  canEditRow = (): boolean => true,
  getRowHighlight,
  filterTabs: filterTabsConfig,
  renderToolbarFilters,
  tableMaxHeight = '65vh',
  includePrint,
  includeExport,
  defaultFilters = [],
  filterCheckboxes = [],
  onClearState = undefined,
  onBulkEditPress,
  bulkActionsSlot,
  onSelectRow,
  selectedRowIDs,
  tableTitle,
  renderRowDetails,
  stickyRowDetails,
  nested,
  hasBulkEditPermission,
  highlightRowDetails,
}: DataTableProps<TRow, TAgg>): ReactElement => {
  const theme = useTheme();
  const headerRef = useRef<HTMLTableSectionElement>(null);

  const ctx = useContext(SlabContext);
  const isPrintingEnabled = ctx.userInfo.hasFlags([Enums.FeatureFlagName.DataTablePrinting]);
  const toast = useToast();

  const filterTabs =
    filterTabsConfig === undefined
      ? undefined
      : { ...filterTabsConfig, aggregations: tableData.aggregations };

  const [expanded, setExpanded] = useState<string[]>([]);
  const onIsExpandedChange = (id: string, newIsExpanded: boolean): void => {
    setExpanded(newIsExpanded ? [...expanded, id] : expanded.filter((value) => value !== id));
  };

  const {
    tableState,
    setSelectedRowIDs,
    setSearchString,
    toggleTabIsActive: toggleFilterTab,
    saveState,
    clearState,
    page,
    setPage,
  } = useTableState<TRow, TAgg>({
    tableName,
    tableData,
    filterCheckboxes,
    filterTabs,
    defaultFilters,
    allColumns: tableData.allColumns,
    toast,
    onClearState,
  });

  useEffect(() => {
    if (selectedRowIDs !== undefined) {
      setSelectedRowIDs(selectedRowIDs);
    }
  }, [selectedRowIDs]);

  // Reset selected row IDs when the page index or column filters change
  useEffect(() => {
    onSelectRow?.([]);
  }, [tableData.currentPage.data]);

  const { renderedColumns } = tableState;

  const searchString = (tableState.searchFilter?.values?.[0] ?? '') as string;

  const { data: pageRows, isFetched, isFetching, isLoading, isSuccess } = tableData.currentPage;

  const toggleCheckBoxFilter = ({ filter }: FilterCheckbox<TRow>): void => {
    if (tableData.getFilter(filter) !== undefined) {
      tableData.removeFilters(filter);
    } else {
      tableData.addFilters(filter);
    }
  };

  const toggleOrder = (current: Enums.SortDirection): Enums.SortDirection =>
    current === Enums.SortDirection.Descending
      ? Enums.SortDirection.Ascending
      : Enums.SortDirection.Descending;

  const handleRequestSort = async (
    event: MouseEvent<unknown>,
    property: NestedKeyOf<TRow>,
  ): Promise<void> => {
    const order =
      tableData.sortConfig?.name === property
        ? toggleOrder(tableData.sortConfig.direction)
        : getInitialSortOrder(renderedColumns, property);
    await tableData.setSortConfig({ direction: order, name: property });
  };

  const handleSelectAllClick = (isChecked: boolean): void => {
    const newSelected = isChecked ? (pageRows ?? []).map((r) => r.id) : [];
    setSelectedRowIDs(newSelected);
    onSelectRow?.(newSelected);
  };

  const handleSelectClick = (rowUuid: string): void => {
    let newSelected: string[];
    if (tableState.selectedRowIDs.includes(rowUuid)) {
      newSelected = tableState.selectedRowIDs.filter((uuid) => uuid !== rowUuid);
    } else {
      newSelected = [...tableState.selectedRowIDs, rowUuid];
    }

    setSelectedRowIDs(newSelected);
    onSelectRow?.(newSelected);
  };

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    await tableData.setRowCountPerPage(parseInt(event.target.value, 10));
  };

  const showRowArchive = hasValue(onArchivePress) && canArchive;
  const hasStickyActions = hasValue(onEditPress) || showRowArchive || hasValue(renderStickyActions);

  let stickyRows: TRow[] | undefined;
  if (constructStickyRows !== undefined) {
    stickyRows = constructStickyRows(tableData.aggregations.data);
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    tableData.pageIndex === 0 ? 0 : tableData.rowCountPerPage - (pageRows ?? []).length;

  // The number of logical columns in the table, including special built-in columns for selection checkboxes
  // or edit buttons.  Note that the necessary colSpan for a full-width table row is twice as large.
  const columnCount = renderedColumns.size + (includeSelect ? 1 : 0) + (hasStickyActions ? 1 : 0);

  const handleColumnFilterChange = async (
    column: Column<TRow>,
    filter?: NamedFilterOpts<TRow>,
  ): Promise<void> => {
    const oldFilter = tableData?.getColumnFilter?.(column.id);

    if (oldFilter !== undefined && filter !== undefined) {
      await tableData.replaceFilters([oldFilter], [filter]);
    } else if (filter !== undefined) {
      await tableData.addFilters(filter);
    }
  };

  const handleColumnFilterRemove = async (column: Column<TRow>): Promise<void> => {
    const oldFilter = tableData?.getColumnFilter?.(column.id);
    if (oldFilter !== undefined) {
      await tableData.removeFilters(oldFilter);
    }
  };

  const userColumnFilters = getColumnFilter({
    filterCheckboxes,
    defaultFilters,
    tableData,
  });

  const columnFilters = userColumnFilters.filter((f) =>
    supportedColumnFilterOperations.includes(f.operation),
  );

  const productCategoryFilter = tableData.getFilter({
    name: 'productCategory' as NestedKeyOf<TRow>,
    operation: Enums.FilterOperation.Equals,
  });

  const allFilters = [
    ...userColumnFilters,
    ...defaultFilters.filter(
      (df) => !userColumnFilters.some((f) => 'name' in f && 'name' in df && f.name === df.name),
    ),
    ...(productCategoryFilter ? [productCategoryFilter] : []),
  ];

  const handleFilterReset = (): void => {
    columnFilters.forEach((filter) => {
      tableData.removeFilters(filter);
    });
  };

  const toolbarFiltersElement = hasValue(renderToolbarFilters)
    ? renderToolbarFilters(tableData)
    : undefined;

  return (
    <>
      {(tableState.tabLabels ?? []).length > 0 && (
        <>
          <FilterTabs
            tabs={tableState.tabLabels ?? []}
            aggregations={tableData.aggregations}
            handleChangeTab={toggleFilterTab}
          />
          {tableState.checkboxMap.size === 0 && <Box paddingBottom='1.25rem' />}
        </>
      )}
      {tableState.checkboxMap.size > 0 && (
        <Box display='flex' justifyContent='flex-end'>
          <Box display='flex' flexDirection='row'>
            {[...tableState.checkboxMap.values()].map(({ config: fcb, enabled }) => (
              <Box key={`datatable-checkbox-${fcb.label}`} display='flex' alignItems='center'>
                <FormControlLabel
                  id={fcb.label}
                  label={fcb.label}
                  control={
                    <Checkbox
                      checked={enabled}
                      inputProps={{
                        'aria-labelledby': fcb.label,
                      }}
                      onClick={(): void => {
                        toggleCheckBoxFilter(fcb);
                      }}
                    />
                  }
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          th: {
            whiteSpace: 'nowrap',
          },
        }}
      >
        {showToolbar && (
          <Toolbar
            columnsFiltersCount={columnFilters.length}
            initialSearchString={searchString}
            onSearch={(v): void => {
              setSearchString(v.toLowerCase());
            }}
            onColumnsFilterIconClick={handleFilterReset}
            allColumns={tableData.allColumns}
            renderedColumns={renderedColumns}
            isFetchingRows={isFetching}
            printOpts={
              isPrintingEnabled && includePrint !== undefined
                ? {
                    getAllRows: tableData.getRowsForAllPagesWithPotentiallyPoorPerformance,
                    tableName: includePrint.title,
                  }
                : undefined
            }
            exportOpts={
              includeExport !== undefined
                ? {
                    getAllRows: tableData.getRowsForAllPagesWithPotentiallyPoorPerformance,
                    allFilters,
                    title: includeExport.title,
                  }
                : undefined
            }
            onColumnChange={tableData.setIsColumnRendered}
            onColumnReorder={(newColumns): void => {
              tableData.setColumnOrder(newColumns.map((col) => col.id));
            }}
            toolbarFiltersSlot={toolbarFiltersElement}
            {...(tableName !== undefined && { tableName })}
            onSave={saveState}
            onClear={clearState}
          />
        )}
        <Paper
          sx={{
            overflow: 'auto',
            mb: nested ? 0 : 2,
            position: 'relative',
            boxShadow: nested ? 0 : undefined,
          }}
        >
          {tableState.selectedRowIDs.length > 0 && (
            <BulkSelectOverlay
              hasBulkEditPermission={hasBulkEditPermission}
              rowCount={pageRows.length}
              onClickEdit={onBulkEditPress}
              bulkActionsSlot={bulkActionsSlot}
              tableTitle={tableTitle}
              selectedRowCount={tableState.selectedRowIDs.length}
              headerHeight={headerRef.current?.offsetHeight}
              onSelectAllClick={handleSelectAllClick}
            />
          )}
          <TableContainer sx={{ maxHeight: tableMaxHeight, containerType: 'inline-size' }}>
            <Table
              aria-labelledby='tableTitle'
              sx={{
                borderCollapse: 'separate',
              }}
            >
              <TableHead
                ref={headerRef}
                typeaheadTableName={typeaheadTableName}
                onColumnFilterChange={showColumnFilter ? handleColumnFilterChange : undefined}
                onColumnFilterRemove={showColumnFilter ? handleColumnFilterRemove : undefined}
                tableFilter={columnFilters}
                allFilters={allFilters}
                headCells={renderedColumns}
                numSelected={tableState.selectedRowIDs.length}
                sortConfig={tableData.sortConfig}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={tableData.rowCount.data}
                includeSelect={includeSelect}
                includeEdit={hasStickyActions}
                sx={{
                  backgroundColor: 'white',
                  zIndex: nested
                    ? (theme.zIndex.tableHead ?? 2) - 1
                    : (theme.zIndex.tableHead ?? 2),
                }}
              />
              <DataTableBody
                isLoading={isLoading}
                isFetched={isFetched}
                isSuccess={isSuccess}
                pageRows={pageRows}
                emptyRows={emptyRows}
                columnCount={columnCount}
                renderedColumns={renderedColumns}
                expanded={expanded}
                onIsExpandedChange={onIsExpandedChange}
                selectedRowIDs={tableState.selectedRowIDs}
                includeSelect={includeSelect}
                onEditPress={onEditPress}
                canEditRow={canEditRow}
                onArchivePress={onArchivePress}
                isRowArchived={isRowArchived}
                canArchive={canArchive}
                renderStickyActions={renderStickyActions}
                getRowHighlight={getRowHighlight}
                renderRowDetails={renderRowDetails}
                stickyRowDetails={stickyRowDetails}
                nested={nested}
                onSelectClick={handleSelectClick}
                highlightRowDetails={highlightRowDetails}
              />
              {stickyRows !== undefined && !!pageRows.length && (
                <TableFooter>
                  {stickyRows.map((stickyRow) => (
                    <TableRow key={`table-sticky-row-${stickyRow.id}`}>
                      {includeSelect === true && (
                        <TableCell
                          padding='checkbox'
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            backgroundColor: theme.palette.common.white,
                          }}
                        />
                      )}
                      <SlabTableRowCells
                        renderedColumns={renderedColumns}
                        row={stickyRow}
                        labelID={`sticky-table-header-${stickyRow.id}`}
                      />
                      {hasStickyActions && (
                        <TableCell
                          padding='checkbox'
                          sx={{
                            position: 'sticky',
                            right: 0,
                            zIndex: 1,
                            backgroundColor: theme.palette.common.white,
                          }}
                        />
                      )}
                    </TableRow>
                  ))}
                </TableFooter>
              )}
            </Table>
          </TableContainer>
          {showFooter ? (
            <TablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTS}
              component='div'
              count={tableData.rowCount.data}
              rowsPerPage={tableData.rowCountPerPage}
              page={page}
              labelDisplayedRows={({ from, to, count }): string =>
                `${from.toLocaleString('en-US')}–${to.toLocaleString('en-US')} of ${count.toLocaleString('en-US')}`
              }
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                backgroundColor: theme.palette.SlabGray['50'],
                '> .MuiToolbar-root': {
                  alignItems: 'baseline',
                  minHeight: 0,
                },
                '& p': {
                  fontSize: 'inherit',
                  lineHeight: 1,
                  margin: 0,
                },
              }}
            />
          ) : null}
        </Paper>
        {/* Necessary box padding - if row count increases, margin + padding are not respected during the expansion */}
        {nested ? null : <Box height='2rem' />}
      </Box>
    </>
  );
};
